import { mutateLedgers } from "@/api/ledgers/useLedgersQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  LedgerWithActualDateViewGet200ResponseRowsInner,
  WmsApiLedgersActualDatePostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface ActualDateUpdateFormProps {
  rowData: LedgerWithActualDateViewGet200ResponseRowsInner;
  refetch: () => void;
}
export const ActualDateUpdateForm = (params: ActualDateUpdateFormProps) => {
  const { rowData, refetch } = params;
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { mutate: actualDateUpdate } = useMutation(
    (params: WmsApiLedgersActualDatePostRequest) =>
      mutateLedgers
        .actualDate(params)
        .mutationFn(params as WmsApiLedgersActualDatePostRequest | any),
    {
      onSuccess: () => {
        closeModal({});
        refetch();
        customAlert(
          t("message.editSuccess", { item: t("ledger.actualDate") }),
          t("common.editSuccess"),
          "green"
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("ledger.actualDate") }),
          t("common.editFail"),
          "red"
        );
      },
    }
  );
  const form = useForm<Partial<WmsApiLedgersActualDatePostRequest>>({
    initialValues: {
      ledgersActualDatePostRequest: {
        actualDate: rowData.actualDate ?? "",
        ledgerId: rowData.id ?? 0,
      },
    },
  });

  const onSubmit = () => {
    try {
      actualDateUpdate({
        ledgersActualDatePostRequest: {
          actualDate: form.values.ledgersActualDatePostRequest?.actualDate ?? "",
          ledgerId: form.values.ledgersActualDatePostRequest?.ledgerId ?? 0,
        },
      });
      refetch();
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("ledger.actualDate") }),
        t("common.editFail"),
        "red"
      );
    }
  };
  return (
    <Flex gap="md" direction="column">
      <DateInput
        locale="ko"
        firstDayOfWeek={0}
        label={t("ledger.actualDate")}
        popoverProps={{
          position: "bottom" as const,
          withinPortal: true,
          offset: 10,
        }}
        clearable
        value={
          form.values.ledgersActualDatePostRequest?.actualDate
            ? new Date(form.values.ledgersActualDatePostRequest.actualDate)
            : null
        }
        onChange={(date) =>
          form.setFieldValue(
            "ledgersActualDatePostRequest.actualDate",
            date ? date.toISOString() : ""
          )
        }
        valueFormat="YYYY-MM-DD"
        maxDate={new Date()}
      />
      <Button onClick={() => onSubmit()}>
        {t("common.edit", {
          item: t("ledger.actualDate"),
        })}
      </Button>
    </Flex>
  );
};
